import React from 'react';
import { Link, usePoll } from '@inertiajs/react';
import { TriangleAlert } from 'lucide-react';
import { Alert } from '../../components/Alert';
import { Button } from '../../components/ui/button';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import Paginator from '../../components/Paginator';
import { useTrans } from '../../hooks/useTrans';
import { getStatusClass, getStatusText } from './importStatus';

export default function Show({
    import: importData, importErrors, hasTemporaryErrors, isTemporaryAbort, pageTranslations,
}) {
    const translate = useTrans();

    const isRunning = !importData.status || importData.status === 'running';
    const isAborted = importData.status === 'failed';
    usePoll(5000, { only: ['import', 'importErrors', 'hasTemporaryErrors', 'isTemporaryAbort'] }, { autoStart: isRunning });

    function getErrorTypeText(type) {
        switch (type) {
            case 'double': return pageTranslations.errorTypeDouble;
            case 'temporary': return pageTranslations.errorTypeTemporary;
            default: return pageTranslations.errorTypeError;
        }
    }

    function getErrorTypeClass(type) {
        switch (type) {
            case 'double': return 'bg-[#ededed] text-[#003545]';
            case 'temporary': return 'bg-yellow-50 text-yellow-800 border border-yellow-300';
            default: return 'bg-red-50 text-red-800 border border-red-300';
        }
    }

    function getNotice() {
        if (isAborted && isTemporaryAbort) {
            return {
                variant: 'warning',
                title: translate('De import is halverwege gestopt door een tijdelijke storing'),
                body: translate('De regels die al zijn ingelezen staan als order klaar. Importeer de overige regels later opnieuw. Blijft het misgaan, neem dan contact op met Customer Service (cs@schotpoort.nl).'),
            };
        }

        if (isAborted) {
            return {
                variant: 'error',
                title: translate('De import is halverwege gestopt door een fout'),
                body: translate('De regels die al zijn ingelezen staan als order klaar. Neem contact op met Customer Service (cs@schotpoort.nl) voordat u de overige regels opnieuw importeert.'),
            };
        }

        if (hasTemporaryErrors) {
            return {
                variant: 'warning',
                title: translate('Een aantal regels is overgeslagen door een tijdelijke storing'),
                body: translate('Deze regels zijn niet afgekeurd: de gegevens zijn in orde, maar ze konden op dat moment niet worden verwerkt. Importeer alleen deze regels later opnieuw. Blijft het misgaan, neem dan contact op met Customer Service (cs@schotpoort.nl).'),
            };
        }

        return null;
    }

    const notice = getNotice();
    const userName = importData.user_name || '-';
    const createdAt = importData.created_at || '-';

    return (
        <div className="max-w-[1200px] mx-auto px-4">
            {notice && (
                <Alert variant={notice.variant}>
                    <div className="flex items-start gap-2">
                        <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0" />
                        <div>
                            <p className="font-bold">{notice.title}</p>
                            <p className="mt-1">{notice.body}</p>
                        </div>
                    </div>
                </Alert>
            )}

            <div className="rounded-[3px] border border-[#bbb] bg-white">
                <div className="p-[30px]">
                    <h3 className="text-2xl font-bold text-[#003545] mb-6">
                        {translate('Importdetails')}
                    </h3>

                    <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                        <div className="flex">
                            <label className="w-[180px] shrink-0 text-sm font-bold text-[#003545]">
                                {translate('Import gestart')}:
                            </label>
                            <span className="text-sm text-[#003545]">
                                {translate('Op :date_time door :name', { date_time: createdAt, name: userName })}
                            </span>
                        </div>

                        <div className="flex">
                            <label className="w-[180px] shrink-0 text-sm font-bold text-[#003545]">
                                {translate('Rijen')}:
                            </label>
                            <span className="text-sm text-[#003545]">{importData.row_count}</span>
                        </div>

                        <div className="flex">
                            <label className="w-[180px] shrink-0 text-sm font-bold text-[#003545]">
                                {translate('Bestandsnaam')}:
                            </label>
                            <span className="text-sm text-[#003545]">{importData.file_name}</span>
                        </div>

                        <div className="flex">
                            <label className="w-[180px] shrink-0 text-sm font-bold text-[#003545]">
                                {translate('Rijen ingelezen')}:
                            </label>
                            <span className="text-sm text-[#003545]">{importData.row_read_count}</span>
                        </div>

                        <div className="flex">
                            <label className="w-[180px] shrink-0 text-sm font-bold text-[#003545]">
                                {translate('Status')}:
                            </label>
                            <span className={`text-sm ${getStatusClass(importData.status)}`}>
                                {getStatusText(importData.status, pageTranslations)}
                            </span>
                        </div>

                        <div className="flex">
                            <label className="w-[180px] shrink-0 text-sm font-bold text-[#003545]">
                                {translate('Rijen met fouten')}:
                            </label>
                            <span className="text-sm text-[#003545]">{importData.row_error_count}</span>
                        </div>

                        <div />

                        <div className="flex">
                            <label className="w-[180px] shrink-0 text-sm font-bold text-[#003545]">
                                {translate('Dubbele orders')}:
                            </label>
                            <span className="text-sm text-[#003545]">{importData.order_double_count}</span>
                        </div>
                    </div>
                </div>

                <div className="border-t border-[#bbb] px-[30px] py-4">
                    <Button variant="clear" asChild>
                        <Link href="/orders-importeren">
                            {translate('Terug naar overzicht')}
                        </Link>
                    </Button>
                </div>
            </div>

            {importErrors?.data?.length > 0 && (
                <div className="mt-6 rounded-[3px] border border-[#bbb] bg-white">
                    <div className="p-[30px]">
                        <h3 className="text-2xl font-bold text-[#003545] mb-4">
                            {translate('Fouten')}
                        </h3>

                        <Table>
                            <TableHeader className="bg-[#ededed] [&_tr]:border-b-2 [&_tr]:border-[#d4d4d4]">
                                <TableRow className="[&_th]:h-auto [&_th]:py-5 [&_th]:px-2.5 [&_th]:text-left [&_th]:text-sm [&_th]:font-normal [&_th]:text-[#003545]">
                                    <TableHead>{translate('Rij')}</TableHead>
                                    <TableHead>{translate('Type')}</TableHead>
                                    <TableHead>{translate('Bericht')}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {importErrors.data.map((error, index) => (
                                    <TableRow key={index} className={`[&_td]:py-5 [&_td]:px-2.5${index % 2 === 1 ? ' bg-[#f5f5f5]' : ''}`}>
                                        <TableCell>{error.row ?? '-'}</TableCell>
                                        <TableCell>
                                            <span className={`inline-block whitespace-nowrap rounded-sm px-2 py-1 text-xs font-bold ${getErrorTypeClass(error.type)}`}>
                                                {getErrorTypeText(error.type)}
                                            </span>
                                        </TableCell>
                                        <TableCell>{error.message}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>

                        <Paginator links={importErrors?.links} />
                    </div>
                </div>
            )}
        </div>
    );
}
